import axiosInstance from "core/api/axiosInstance";

// ==================== FUNCIONES DE UTILIDAD ====================

const getAuthInfo = () => {
  try {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    return {
      hasToken: !!token,
      token: token || null,
      tokenLength: token ? token.length : 0,
      hasUser: !!user,
      userData: user ? JSON.parse(user) : null,
    };
  } catch (error) {
    console.error("Error obteniendo información de autenticación:", error);
    return {
      hasToken: false,
      token: null,
      tokenLength: 0,
      hasUser: false,
      userData: null,
    };
  }
};

const handleServiceError = (error, serviceName) => {
  console.error(`❌ Error en ${serviceName}:`, error);

  let errorMessage = `Error en ${serviceName}`;
  let errorDetails = {};

  if (error.response) {
    const backendMessage = error.response.data?.message || error.response.data?.msm || "";
    const backendError = error.response.data?.error || "";

    errorMessage =
      backendMessage ||
      backendError ||
      `Error ${error.response.status}: ${error.response.statusText || "Error desconocido"}`;

    errorDetails = {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      headers: error.response.headers,
    };

    if (error.response.status === 401) {
      errorMessage =
        backendMessage || "No autorizado. Su sesión ha expirado o el token es inválido.";
    } else if (error.response.status === 403) {
      errorMessage =
        backendMessage || "Acceso denegado. No tiene permisos para realizar esta acción.";
    } else if (error.response.status === 404) {
      errorMessage = "Endpoint no encontrado.";
    } else if (error.response.status >= 500) {
      errorMessage = backendMessage || "Error interno del servidor.";
    }
  } else if (error.request) {
    errorMessage =
      "La solicitud está tomando más tiempo de lo esperado. El procesamiento podría continuar en segundo plano.";
    errorDetails = { request: error.request, isTimeout: true };
  } else {
    errorMessage = error.message || "Error de configuración de la solicitud";
    errorDetails = { message: error.message, stack: error.stack };
  }

  return {
    error: true,
    message: errorMessage,
    details: errorDetails,
    shouldLogout: false,
    isTimeout: error.code === "ECONNABORTED" || error.message?.includes("timeout"),
  };
};

// ==================== FUNCIONES PARA EXTRAER ESTADO DEL PAGO ====================

const extractPaymentStatus = (item) => {
  if (item.payment?.data && Array.isArray(item.payment.data) && item.payment.data.length > 0) {
    const paymentData = item.payment.data[0];
    if (paymentData.status) {
      return paymentData.status;
    }
  }

  if (item.status_invoice) {
    return item.status_invoice;
  }

  return item.status || "pendiente";
};

// Función para obtener el ID de pago - CORREGIDA
// Prioriza pay-* sobre lnk-*
const extractPaymentId = (item) => {
  // Buscar en payment.data (pago con tarjeta)
  if (item.payment?.data && Array.isArray(item.payment.data) && item.payment.data.length > 0) {
    const paymentData = item.payment.data[0];
    // ID de pago con tarjeta (pay-*)
    if (paymentData.id && paymentData.id.startsWith("pay-")) {
      return paymentData.id;
    }
    if (paymentData.payment_id && paymentData.payment_id.startsWith("pay-")) {
      return paymentData.payment_id;
    }
  }

  // Buscar en payment directo (puede ser link o pay)
  if (item.payment) {
    // Si es un link (lnk-*)
    if (item.payment.id && item.payment.id.startsWith("lnk-")) {
      return item.payment.id;
    }
    // Si es un pago con tarjeta (pay-*)
    if (item.payment.id && item.payment.id.startsWith("pay-")) {
      return item.payment.id;
    }
    if (item.payment.payment_id && item.payment.payment_id.startsWith("pay-")) {
      return item.payment.payment_id;
    }
  }

  return null;
};

// Función para determinar el tipo de ID de pago
const getPaymentIdType = (paymentId) => {
  if (!paymentId) return "none";
  if (paymentId.startsWith("pay-")) return "payment"; // Pago con tarjeta
  if (paymentId.startsWith("lnk-")) return "link"; // Link/PSE
  return "unknown";
};

const getFinalStatus = (status) => {
  const statusUpper = (status || "").toUpperCase();

  const approvedStatuses = [
    "APPROVED",
    "AUTHORIZED",
    "SETTLED",
    "COMPLETED",
    "CAPTURED",
    "pagado",
    "PAGADO",
  ];

  if (approvedStatuses.includes(statusUpper)) {
    return "aprobado";
  }

  return "pendiente";
};

// ==================== SERVICIO PARA VERIFICAR FACTURAS EXISTENTES ====================

export const getAllExistingInvoices = async () => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 15000,
    };

    const response = await axiosInstance.get("/api/financial/get-data", config);
    const { data, status } = response;

    if (!data || status !== 200) {
      return { success: false, error: "No se pudieron obtener los datos", invoices: [] };
    }

    const financialData = data.data || [];

    const invoiceNumbers = financialData
      .map((item) => item.invoice_number)
      .filter((num) => num && num !== "N/A")
      .map((num) => num.toString().trim());

    return {
      success: true,
      invoices: financialData,
      invoiceNumbers: [...new Set(invoiceNumbers)],
      total: financialData.length,
    };
  } catch (error) {
    console.error("❌ Error en getAllExistingInvoices:", error);
    return {
      success: false,
      error: error.message,
      invoices: [],
      invoiceNumbers: [],
    };
  }
};

export const verifyInvoicesUploaded = async (companyName = null, timeWindowMinutes = 5) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 10000,
    };

    const params = {};
    if (companyName) {
      params.company = companyName;
    }

    let url = "/api/financial/get-data";
    const queryParams = new URLSearchParams();
    if (params.company) queryParams.append("company", params.company);
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const response = await axiosInstance.get(url, config);
    const { data, status } = response;

    if (!data || status !== 200) {
      return { success: false, error: "No se pudieron obtener los datos" };
    }

    const financialData = data.data || [];

    const now = new Date();
    const timeAgo = new Date(now.getTime() - timeWindowMinutes * 60 * 1000);

    const recentInvoices = financialData.filter((item) => {
      if (!item.createdAt) return false;
      const createdAt = new Date(item.createdAt);
      return createdAt >= timeAgo;
    });

    return {
      success: true,
      count: recentInvoices.length,
      invoices: recentInvoices,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("❌ Error en verifyInvoicesUploaded:", error);
    return {
      success: false,
      error: error.message,
      count: 0,
      invoices: [],
    };
  }
};

// ==================== SERVICIO PARA ENVIAR ARCHIVO EXCEL ====================

export const setInvoiceServiceWithRetry = async (file, options = {}) => {
  const { retryCount = 2, retryDelay = 3000, onProgress = null } = options;

  let lastError = null;

  for (let attempt = 0; attempt <= retryCount; attempt++) {
    try {
      if (onProgress) {
        onProgress(10 + attempt * 20, `Intento ${attempt + 1} de ${retryCount + 1}...`);
      }

      const result = await setInvoiceService(file);

      if (result.success) {
        if (onProgress) onProgress(100, "Completado");
        return result;
      }

      lastError = result;

      if (!result.isTimeout && attempt < retryCount) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        continue;
      }

      return result;
    } catch (error) {
      lastError = { success: false, error: error.message, isTimeout: true };

      if (attempt < retryCount) {
        if (onProgress) onProgress(10 + attempt * 20, `Reintentando después de timeout...`);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }
  }

  if (onProgress) onProgress(80, "Verificando si las facturas se procesaron...");

  await new Promise((resolve) => setTimeout(resolve, 5000));

  const verification = await verifyInvoicesUploaded(null, 10);

  if (verification.success && verification.count > 0) {
    return {
      success: true,
      message: `El archivo se procesó correctamente.`,
      importedCount: verification.count,
      data: { processed: verification.count, errors: 0 },
      verified: true,
      invoices: verification.invoices,
    };
  }

  return {
    success: false,
    error: lastError?.error || "No se pudo procesar el archivo después de múltiples intentos",
    isTimeout: true,
  };
};

export const setInvoiceService = async (file) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    if (!file) {
      throw new Error("Debe seleccionar un archivo para enviar.");
    }

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      throw new Error(
        "Formato de archivo no válido. Solo se permiten archivos Excel (.xlsx, .xls)"
      );
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(`El archivo es demasiado grande. Tamaño máximo: 10MB`);
    }

    const formData = new FormData();
    formData.append("file", file);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 60000,
    };

    const response = await axiosInstance.post("/api/financial/set-invoice", formData, config);
    const { data, status } = response;

    if (!data) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    const isSuccess = status >= 200 && status < 300;
    const responseMessage = data.msm || data.message || "";

    if (!isSuccess) {
      throw new Error(responseMessage || "Error al procesar el archivo Excel");
    }

    const resultData = data.data || {};

    return {
      success: true,
      message: responseMessage || "Archivo procesado exitosamente",
      data: {
        processed: resultData.processed || 0,
        errors: resultData.errors || 0,
        details: resultData.details || [],
      },
      importedCount: resultData.processed || 0,
      hasErrors: (resultData.errors || 0) > 0,
      errors: resultData.errors || [],
      timestamp: new Date().toISOString(),
      responseStatus: status,
    };
  } catch (error) {
    console.error("❌ Error en setInvoiceService:", error);
    const errorResult = handleServiceError(error, "setInvoiceService");

    if (errorResult.isTimeout) {
      return {
        success: false,
        error: errorResult.message,
        isTimeout: true,
        mightHaveProcessed: true,
        details: errorResult.details,
        timestamp: new Date().toISOString(),
      };
    }

    return {
      success: false,
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

export const setInvoiceServiceSmart = async (file, onProgress) => {
  const isLargeFile = file.size > 1 * 1024 * 1024;

  if (onProgress) onProgress(5, null);

  if (isLargeFile) {
    if (onProgress) onProgress(10, null);
    const result = await setInvoiceServiceWithRetry(file, {
      retryCount: 2,
      retryDelay: 3000,
      onProgress: (progress, message) => {
        if (onProgress) onProgress(10 + progress * 0.7, message);
      },
    });
    return result;
  } else {
    try {
      const result = await setInvoiceService(file);
      if (result.success) {
        if (onProgress) onProgress(100, null);
        return result;
      }

      if (result.isTimeout) {
        if (onProgress) onProgress(70, null);
        await new Promise((resolve) => setTimeout(resolve, 5000));

        const verification = await verifyInvoicesUploaded(null, 10);
        if (verification.success && verification.count > 0) {
          if (onProgress) onProgress(100, null);
          return {
            success: true,
            message: `El archivo se procesó correctamente.`,
            importedCount: verification.count,
            verified: true,
          };
        }
      }

      return result;
    } catch (error) {
      if (onProgress) onProgress(70, null);
      await new Promise((resolve) => setTimeout(resolve, 5000));

      const verification = await verifyInvoicesUploaded(null, 10);
      if (verification.success && verification.count > 0) {
        if (onProgress) onProgress(100, null);
        return {
          success: true,
          message: `El archivo se procesó correctamente.`,
          importedCount: verification.count,
          verified: true,
        };
      }

      return { success: false, error: error.message };
    }
  }
};

// ==================== SERVICIO PARA OBTENER DATOS FINANCIEROS ====================

export const getFinancialDataService = async (params = {}) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 15000,
    };

    let url = "/api/financial/get-data";
    if (Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append("status", params.status);
      if (params.company) queryParams.append("company", params.company);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await axiosInstance.get(url, config);
    const { data, status } = response;

    if (!data || status !== 200) {
      throw new Error("No se pudieron obtener los datos financieros");
    }

    const responseMessage = data.msm || data.message || "";
    const financialData = data.data || [];

    const processedData = financialData.map((item) => {
      let formattedDate = "N/A";
      if (item.createdAt) {
        try {
          const date = new Date(item.createdAt);
          formattedDate = date.toLocaleDateString("es-CO", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        } catch (e) {
          formattedDate = item.createdAt;
        }
      }

      const paymentStatus = extractPaymentStatus(item);
      const finalStatus = getFinalStatus(paymentStatus);
      const paymentId = extractPaymentId(item);
      const paymentIdType = getPaymentIdType(paymentId);

      let amount = parseFloat(item.amount) || 0;
      if (item.payment?.data && Array.isArray(item.payment.data) && item.payment.data.length > 0) {
        const paymentData = item.payment.data[0];
        if (paymentData.amount && !amount) {
          amount = parseFloat(paymentData.amount) || 0;
        }
      }

      let statusDetail = null;
      if (item.payment?.data && Array.isArray(item.payment.data) && item.payment.data.length > 0) {
        const paymentData = item.payment.data[0];
        if (paymentData.status_detail) {
          statusDetail = paymentData.status_detail;
        }
        if (paymentData.transactions && paymentData.transactions.length > 0) {
          const authTransaction = paymentData.transactions.find((t) => t.type === "AUTHORIZATION");
          if (authTransaction && authTransaction.status_detail) {
            statusDetail = authTransaction.status_detail;
          }
        }
      }

      return {
        id: item.id,
        invoiceNumber: item.invoice_number || "N/A",
        invoice_number: item.invoice_number || "N/A",
        nit: item.nit || "N/A",
        company: item.company || "N/A",
        codigoEmpresarial: item.code || item.codigo_empresarial || "N/A",
        status: finalStatus,
        statusOriginal: paymentStatus,
        statusInvoice: item.status_invoice,
        statusDetail: statusDetail,
        amount: amount,
        paymentId: paymentId,
        paymentIdType: paymentIdType, // "payment", "link", o "none"
        userId: item.user_id,
        payment: item.payment,
        more: item.more,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        deletedAt: item.deletedAt,
        createdAtFormatted: formattedDate,
        statusBadge: {
          text: finalStatus === "aprobado" ? "Aprobado" : "Pendiente",
          color: finalStatus === "aprobado" ? "success" : "warning",
        },
      };
    });

    return {
      success: true,
      data: processedData,
      rawData: financialData,
      total: financialData.length,
      message: responseMessage || "Datos financieros obtenidos exitosamente",
      timestamp: new Date().toISOString(),
      responseStatus: status,
    };
  } catch (error) {
    console.error("❌ Error en getFinancialDataService:", error);
    const errorResult = handleServiceError(error, "getFinancialDataService");
    return {
      success: false,
      data: [],
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIOS CRUD ====================

export const updateInvoiceService = async (id, data) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    if (!id) {
      throw new Error("El ID de la factura es requerido");
    }

    if (!data.invoice_number || !data.invoice_number.trim()) {
      throw new Error("El número de factura es requerido");
    }
    if (!data.nit || !data.nit.trim()) {
      throw new Error("El NIT es requerido");
    }
    if (!data.company || !data.company.trim()) {
      throw new Error("La empresa es requerida");
    }
    if (!data.amount || data.amount <= 0) {
      throw new Error("El monto debe ser mayor a 0");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 15000,
    };

    const payload = {
      id: id,
      invoice_number: data.invoice_number.trim(),
      nit: data.nit.trim(),
      company: data.company.trim(),
      amount: Number(data.amount),
      code: data.code ? data.code.trim() : "",
      status: "pendiente",
    };

    const response = await axiosInstance.put("/api/financial/update-invoice", payload, config);
    const { data: responseData, status } = response;

    if (!responseData) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    const isSuccess = status >= 200 && status < 300;
    const responseMessage = responseData.msm || responseData.message || "";

    if (!isSuccess) {
      throw new Error(responseMessage || "Error al actualizar la factura");
    }

    return {
      success: true,
      data: responseData.data || null,
      message: responseMessage || "Factura actualizada exitosamente",
      timestamp: new Date().toISOString(),
      responseStatus: status,
    };
  } catch (error) {
    console.error("❌ Error en updateInvoiceService:", error);
    const errorResult = handleServiceError(error, "updateInvoiceService");
    return {
      success: false,
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

export const deleteInvoiceService = async (id) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    if (!id) {
      throw new Error("El ID de la factura es requerido");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 15000,
    };

    const payload = { id: id };
    const response = await axiosInstance.delete("/api/financial/delete-invoice", {
      data: payload,
      ...config,
    });
    const { data, status } = response;

    if (!data) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    const isSuccess = status >= 200 && status < 300;
    const responseMessage = data.msm || data.message || "";

    if (!isSuccess) {
      throw new Error(responseMessage || "Error al eliminar la factura");
    }

    return {
      success: true,
      message: responseMessage || "Factura eliminada exitosamente",
      timestamp: new Date().toISOString(),
      responseStatus: status,
    };
  } catch (error) {
    console.error("❌ Error en deleteInvoiceService:", error);
    const errorResult = handleServiceError(error, "deleteInvoiceService");
    return {
      success: false,
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

export default {
  getFinancialDataService,
  setInvoiceService,
  setInvoiceServiceWithRetry,
  setInvoiceServiceSmart,
  verifyInvoicesUploaded,
  updateInvoiceService,
  deleteInvoiceService,
  getAllExistingInvoices,
};
